import {
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getAreaRangeData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
  getDataSources,
  magnitudePointFormatter,
} from '../utils/compass'
import { range6m1yAll } from '../utils/config'
import { Palette } from '../utils/constants'
import { CommonChartConfig, commonChartOptions } from './common'
import type { BaseContext } from './'

const tooltip = {
  pointFormatter: magnitudePointFormatter(),
  xDateFormat: '%b',
}

export namespace PulpAndPaper {
  export namespace Shipments {
    const CONTEXT_ID = 'equity:pulp_and_paper:shipments'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'total' | 'six_months_yoy' | 'pulp_growth'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'total':
            return getTotalProps(config, data)
          case 'six_months_yoy':
            return getSixMonthsYoyProps(config, data)
          case 'pulp_growth':
            return getPulpGrowthProps(config, data)
        }
      },
    }

    const getTotalProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[4].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[5].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const series = [
        {
          data: convertCompassToHighchartsDataFormat(data.data[0].data),
          id: `${config.chart}-0`,
          lineWidth: 1.25,
          name: data.data[0].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[1].data),
          id: `${config.chart}-1`,
          lineWidth: 1.25,
          name: data.data[1].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[2].data),
          id: `${config.chart}-2`,
          lineWidth: 3,
          name: data.data[2].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          dashStyle: 'LongDash',
          data: convertCompassToHighchartsDataFormat(data.data[3].data),
          id: `${config.chart}-avg`,
          name: 'Mean 2010-2016',
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          color: Palette.fadedYellow,
          data: getAreaRangeData(rangeValues),
          enableMouseTracking: false,
          grouping: false,
          id: `${config.chart}-range`,
          name: 'Range 2010-2016',
          type: 'arearange',
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]

      const xAxis = { labels: { format: '{value:%b}' } }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          xAxis,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getSixMonthsYoyProps = getMultipleLineChartProps(Context, {
      addMinMaxIndicators: true,
      convertToPercentages: [true, true, true],
      tooltip: { xDateFormat: '%B %Y' },
    })

    const getPulpGrowthProps = getMultipleLineChartProps(Context, {
      tooltip: {
        shared: true,
        split: false,
        pointFormatter: magnitudePointFormatter(),
        xDateFormat: '%B %Y',
      },
    })
  }

  export namespace Estoques {
    const CONTEXT_ID = 'equity:pulp_and_paper:estoques'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'total_stocks' | 'total_supply'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'total_stocks':
            return getTotalStocksProps(config, data)
          case 'total_supply':
            return getTotalSupplyProps(config, data)
        }
      },
    }

    const getTotalStocksProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[4].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[5].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const series = [
        {
          data: convertCompassToHighchartsDataFormat(data.data[0].data),
          id: `${config.chart}-0`,
          lineWidth: 1.25,
          name: data.data[0].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[1].data),
          id: `${config.chart}-1`,
          lineWidth: 1.25,
          name: data.data[1].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[2].data),
          id: `${config.chart}-2`,
          lineWidth: 3,
          name: data.data[2].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          dashStyle: 'LongDash',
          data: convertCompassToHighchartsDataFormat(data.data[3].data),
          id: `${config.chart}-avg`,
          name: 'Mean 2010-2016',
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          color: Palette.fadedYellow,
          data: getAreaRangeData(rangeValues),
          enableMouseTracking: false,
          grouping: false,
          id: `${config.chart}-range`,
          name: 'Range 2010-2016',
          type: 'arearange',
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]

      const xAxis = { labels: { format: '{value:%b}' } }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          xAxis,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getTotalSupplyProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [true, true],
        msdIndicators: { avg: true, sd1: true, sd2: true },
        tooltip: {
          pointFormatter: magnitudePointFormatter({ unit: '%' }),
          xDateFormat: '%B %Y',
        },
      },
      {
        showInLegend: [false, ...Array(5).fill(false)],
        type: ['spline'],
        visible: [true, ...Array(5).fill(false)],
      }
    )
  }

  export namespace Utilizacao {
    const CONTEXT_ID = 'equity:pulp_and_paper:utilizacao'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'ship_capacity' | 'three_months'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ship_capacity':
            return getShipCapacityProps(config, data)
          case 'three_months':
            return getThreeMonthsProps(config, data)
        }
      },
    }

    const getShipCapacityProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[4].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[5].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const series = [
        {
          data: convertCompassToHighchartsDataFormat(data.data[0].data),
          id: `${config.chart}-0`,
          lineWidth: 1.25,
          name: data.data[0].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[1].data),
          id: `${config.chart}-1`,
          lineWidth: 1.25,
          name: data.data[1].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          data: convertCompassToHighchartsDataFormat(data.data[2].data),
          id: `${config.chart}-2`,
          lineWidth: 3,
          name: data.data[2].name,
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          dashStyle: 'LongDash',
          data: convertCompassToHighchartsDataFormat(data.data[3].data),
          id: `${config.chart}-avg`,
          name: 'Mean 2010-2016',
          tooltip,
          type: 'spline',
          yAxis: 'default',
          zIndex: 1,
        },
        {
          color: Palette.fadedYellow,
          data: getAreaRangeData(rangeValues),
          enableMouseTracking: false,
          grouping: false,
          id: `${config.chart}-range`,
          name: 'Range 2010-2016',
          type: 'arearange',
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]

      const xAxis = { labels: { format: '{value:%b}' } }

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          xAxis,
          yAxis,
        },
        stockTools: true,
      }
    }

    const getThreeMonthsProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        convertToPercentages: [true, true],
        msdIndicators: { avg: true, sd1: true, sd2: true },
        tooltip: {
          pointFormatter: magnitudePointFormatter({ unit: '%' }),
          xDateFormat: '%B %Y',
        },
      },
      {
        showInLegend: [false, ...Array(5).fill(false)],
        type: ['spline'],
        visible: [true, ...Array(5).fill(false)],
      }
    )
  }

  export namespace Demanda {
    const CONTEXT_ID = 'equity:pulp_and_paper:demanda'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'demanda_capacidade'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            convertToPercentages: [true, true],
            msdIndicators: { avg: true, sd1: true, sd2: true },
            tooltip: { xDateFormat: '%B %Y' },
          },
          {
            showInLegend: [true, true, ...Array(10).fill(false)],
            type: ['spline', 'spline'],
            visible: [true, true, ...Array(10).fill(false)],
          }
        )(config, data),
    }
  }

  export namespace Precos {
    const CONTEXT_ID = 'equity:pulp_and_paper:precos'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'china_hard_wood'
        | 'china_hard_wood_spot'
        | 'china_euca_euro_pe_pebhkp'
        | 'soft_hard'
        | 'foex_hard_wood'
        | 'margens_papel_china'
        | 'margens_papel_europa'
        | 'soft_wood_china'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'china_hard_wood':
          case 'soft_hard':
          case 'foex_hard_wood':
          case 'soft_wood_china':
          case 'margens_papel_china':
          case 'margens_papel_europa':
            return getMultipleLineChartProps(Context, {
              addMinMaxIndicators: true,
              tooltip: { pointFormatter: magnitudePointFormatter() },
              yAxis: [
                {
                  title: {
                    text:
                      config.chart === 'china_hard_wood' ||
                      config.chart === 'soft_hard' ||
                      config.chart === 'foex_hard_wood' ||
                      config.chart === 'soft_wood_china'
                        ? '$/t'
                        : config.chart === 'margens_papel_europa'
                        ? 'FBB - BHKP Spot'
                        : '',
                  },
                },
              ],
            })(config, data)
          case 'china_hard_wood_spot':
          case 'china_euca_euro_pe_pebhkp':
            return getMultipleLineChartProps(
              Context,
              {
                addMinMaxIndicators: true,
                msdIndicators: { avg: true, sd1: true, sd2: true },
              },
              {
                showInLegend: [false, false, false, false, false, false],
                visible: [true, false, false, false, false, false],
              }
            )(config, data)
        }
      },
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:pulp_and_paper:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_ebitda_suzb3'
        | 'ev_ebitda_ratio_suzb3_peers'
        | 'ev_ebitda_klbn3'
        | 'ev_ebitda_ratio_klbn3_peers'
        | 'ev_ebitda_peers_suzb3'
        | 'ev_ebitda_peers_klbn3'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_suzb3':
            return getChartProps(config, data, 'EV/EBITDA F12m Suzano')
          case 'ev_ebitda_ratio_suzb3_peers':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m Suzano / Peers (Klabin, CMPC, Stora, IP, Domtar)',
              ['Ratio', 'Suzano', 'Peers Suzano']
            )
          case 'ev_ebitda_klbn3':
            return getChartProps(config, data, 'EV/EBITDA F12m Klabin')
          case 'ev_ebitda_ratio_klbn3_peers':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m Klabin / Peers (IP, Mondi, PKG, Westrock, GPK, Domtar)',
              ['Ratio', 'Klabin', 'Peers Klabin']
            )
          case 'ev_ebitda_peers_suzb3':
            return getChartProps(
              config,
              data,
              'EV/EBITDA F12m Peers Suzano (Klabin, CMPC, Stora, IP, Domtar)'
            )
          case 'ev_ebitda_peers_klbn3':
            return getChartProps(
              config,
              data,
              'EV/EBITDA F12m Peers Klabin (IP, Mondi, PKG, Westrock, GPK, Domtar)'
            )
        }
      },
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
          msdIndicatorsSeriesIndexes: [0],
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name: seriesNames,
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }
}
